import { Box, Button, Container, SxProps, Typography } from '@mui/material';
import React, { useState } from 'react';

import { IconHeartBroken } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';

type Props = React.PropsWithChildren<{
  sx?: SxProps;
  context?: string;
}>;

interface State {
  hasError: boolean;
  error: Error | null;
}

// This is a test error button for development purposes, expected to be unused most of the time
export function TestErrorButton() {
  const [throwError, setThrowError] = useState(false);
  const ErrorComponent = () => {
    throw new Error('Test error');
  };
  return (
    <Button onClick={() => setThrowError(true)}>
      {throwError && <ErrorComponent />}
      Test Error
    </Button>
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  callContext: string | undefined = undefined;

  constructor(props: Props) {
    super(props);
    this.callContext = props.context;
    // eslint-disable-next-line react/no-unused-state
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService(error: Error, errorInfo: React.ErrorInfo) {
    try {
      Sentry.captureException(error, {
        extra: {
          context: this.callContext,
          url: window.location.href,
          errorAsString: `${error}\n${errorInfo}`,
          errorAsJson: JSON.stringify({ error, errorInfo }),
          stack: error.stack,
          message: error.message,
        },
      });
    } catch (error_) {
      // eslint-disable-next-line no-console
      console.error(error_);
    }
  }

  render() {
    const { hasError } = this.state;
    const { sx, children } = this.props;
    if (hasError) {
      const description = (
        <div>
          Try{' '}
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            reloading the page
          </Button>
          <br />
          If the problem persists, please contact us at{' '}
          <a href="mailto:success@cofactr.com">success@cofactr.com</a>.
        </div>
      );
      return (
        <Box
          sx={{
            m: 4,
            textAlign: 'center',
            width: '100%',
            height: '100%',
            ...sx,
          }}
        >
          <NonIdealState
            title="Oops! Something went wrong."
            description={description}
          />
        </Box>
      );
    }

    return children;
  }
}

export function NonIdealState({
  sx,
  title,
  description,
}: {
  title?: string;
  description?: string | JSX.Element;
  sx?: SxProps;
}) {
  return (
    <Container sx={{ ...sx, textAlign: 'center' }}>
      <Box>
        <IconHeartBroken stroke={1.5} size="5rem" />
      </Box>
      {title && (
        <Typography variant="h6" sx={{ my: '6px' }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="subtitle2">{description}</Typography>
      )}
    </Container>
  );
}
